import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ChicagoStatus, ChicagoStatusText } from '@core/util/chicago';
import {
	BusinessMatterClient,
	Matter,
	MatterTitle,
	PersonMatterClient,
} from '@models/matter';
import { PropertyUsage } from '@models/title-insurance';
import { MatterMortgageWithAssociations } from '@models/mortgage';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select } from '@ngxs/store';
import { MatterClients } from '@state/global/matter_clients';
import { MatterDispatch } from '@state/matter';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { MatterMortgagesState } from '@state/matter/matter_mortgages';
import { combineLatest, firstValueFrom, Observable } from 'rxjs';
import { Step } from 'src/app/shared/atomic/molecules/step-progress-nav/step-progress-nav.component';
import { ICON } from '@core/util/icon';
import { AddTitleInsuranceComponent } from '../components/add-title-insurance/add-title-insurance.component';
import { MissingFieldSection } from '../fct/services/validate-fct-data.service';
import {
	ChicagoPortalComponent,
	ChicagoPortalData,
} from './chicago-portal/chicago-portal.component';
import {
	CancelModalComponent,
	CancelModalData,
} from './components/cancel-modal/cancel-modal.component';
import { ValidateChicagoDataService } from './services/validate-chicago-data.service';
import { take } from 'rxjs/operators';
import { MatterManualTitleInsurance } from '@models/title-insurance';
import { MatterEncumbrancesState } from '@state/matter/encumbrances';
import { Encumbrance } from '@models/encumbrance';
import { BusinessDispatch, BusinessState } from '@state/business';
import { ConfirmFirmModalComponent } from 'src/app/private/profile/integrations/chicago/confirm-firm-modal/confirm-firm-modal.component';
import { MatterChicagoTitleInsurance } from '@models/title-insurance/chicago';
import LogRocket from 'logrocket';
import { checkForDuplicatePriority } from '@core/util/title-insurance';
import { ActivatedRoute } from '@angular/router';
import { NameFormattingSettings } from '@models/businessSettings/settings';
import { MatterTitlesState } from '@state/matter/titles/titles.state';
import { isRegionOntario } from '@core/util/address.utils';
import { TestHooks } from './chicago.hooks';
import { ChicagoFunctions } from 'src/app/shared/functions/chicago.service';

const Steps: Step[] = [
	{ title: 'Preparing' },
	{ title: 'Submitted' },
	{ title: 'Processing' },
	{ title: 'Completed' },
];

export const BlankTitleInsurance: MatterManualTitleInsurance = {
	basePremium: 0,
	companyName: '',
	credits: [],
	documents: [],
	expiresOn: '',
	extendCreditAmount: 0,
	fees: [],
	effectiveDate: '',
	lastUpdated: 0,
	orderedOn: '',
	policies: [],
	portalUrl: '',
	premium: 0,
	premiumWithCounselCreditApplied: 0,
	provider: null,
	quote: null,
	referenceNumber: '',
	state: null,
	thirdPartyId: '',
};

@UntilDestroy()
@Component({
	selector: 'app-chicago',
	templateUrl: './chicago.component.html',
	styleUrls: ['./chicago.component.scss'],
	standalone: false,
})
export class ChicagoComponent implements OnInit {
	testHooks = TestHooks;
	public currentStep = 0;
	public missing: MissingFieldSection[] = [];
	public ICON = ICON;
	public status: ChicagoStatus | 0 = 0;
	public ChicagoStatus = ChicagoStatus;
	public statusText;
	public steps = Steps;
	public titleInsurance: MatterChicagoTitleInsurance;
	public blankTitleInsurance = BlankTitleInsurance;

	public orderValid = false;
	public isCommercial = false;
	public outOfOntario = false;

	public loading = true;
	public showPriorityWarning: boolean;

	@SelectSnapshot(SelectedMatter.titleInsuranceThirdPartyId)
	thirdPartyId: number;
	@SelectSnapshot(SelectedMatter.id) matterId: string;
	@SelectSnapshot(BusinessState.chicagoFirmName) chicagoLawfirm: string;
	@SelectSnapshot(BusinessState.getNameFormattingSettings)
	nameFormattingSettings: NameFormattingSettings;
	@Select(SelectedMatter.getSelected) matter$: Observable<Matter>;
	@Select(MatterClients.get_mortgagorsWithAssociationMapped)
	clients$: Observable<(PersonMatterClient | BusinessMatterClient)[]>;

	@Select(MatterMortgagesState.getAllMortgagesWithAssociations)
	mortgages$: Observable<MatterMortgageWithAssociations[]>;
	@Select(MatterEncumbrancesState.getRemain) remainEncumbrances$: Observable<
		Encumbrance[]
	>;
	@Select(SelectedMatter.getTitleInsurance)
	titleInsurance$: Observable<MatterChicagoTitleInsurance>;
	@Select(SelectedMatter.getTitleInsuranceState) state$: Observable<number>;
	@Select(MatterTitlesState.getAll) matterTitles$: Observable<MatterTitle[]>;
	@Select(SelectedMatter.titleInsuranceEffectiveDate)
	effectiveDate$: Observable<string>;

	constructor(
		private route: ActivatedRoute,
		private dialog: MatDialog,
		private chicagoFunctions: ChicagoFunctions,
		private validateChicagoDataService: ValidateChicagoDataService,
		private matterDispatch: MatterDispatch,
		private businessDispatch: BusinessDispatch,
	) {}

	ngOnInit(): void {
		this.state$.pipe(take(1)).subscribe(async (_state) => {
			if (_state && _state !== ChicagoStatus.NOT_SUBMITTED) {
				this.loading = true;
				try {
					await firstValueFrom(
						this.chicagoFunctions.getAllStatusDetails(
							this.thirdPartyId,
							this.matterId,
						),
					);
				} catch (e) {
					LogRocket.log('Error getting Chicago status details', e);
				} finally {
					this.loading = false;
				}
			}

			this.loading = false;
		});

		combineLatest([
			this.matter$,
			this.clients$,
			this.mortgages$,
			this.remainEncumbrances$,
			this.titleInsurance$,
			this.matterTitles$,
		])
			.pipe(untilDestroyed(this))

			.subscribe(
				([
					_matter,
					_clients,
					_mortgages,
					_remainEncumbrances,
					_titleInsurance,
					_matterTitles,
				]) => {
					if (!_matter) {
						return;
					}

					this.isCommercial =
						_matter.propertyDetails?.propertyType?.usage ===
						PropertyUsage.COMMERCIAL;

					this.outOfOntario = !isRegionOntario(
						_matter?.propertyDetails?.address?.province,
					);

					const missingOrderFields =
						this.validateChicagoDataService.getSubmitDataMissingSections(
							_matter,
							_clients,
							_mortgages,
							_remainEncumbrances,
							this.nameFormattingSettings,
							_matterTitles,
						);

					this.orderValid =
						missingOrderFields?.length === 0 &&
						!this.isCommercial &&
						!this.outOfOntario;

					this.showPriorityWarning = !!checkForDuplicatePriority(
						_mortgages,
						_remainEncumbrances,
					);

					if (!this.orderValid) {
						LogRocket.log('Missing fields', missingOrderFields);
					}

					this.missing = missingOrderFields;

					this.titleInsurance = _titleInsurance;
					if (this.titleInsurance.state) {
						this.status = this.titleInsurance.state as ChicagoStatus;
						this.statusText = ChicagoStatusText[this.status.toString()];
						this.setCurrentStep(this.status as number);
						this.changeStepperColor();
						if (this.status === ChicagoStatus.CANCELLED) {
							this.steps.pop();
							this.steps.push({ title: 'Cancelled', failed: true });
							this.changeStepperColor('failed');
						}
						if (
							this.status === ChicagoStatus.WAITING_FOR_INFORMATION ||
							(this.status === ChicagoStatus.NOT_SUBMITTED && !this.orderValid)
						) {
							this.changeStepperColor('warning');
						}
					}
				},
			);

		this.route.queryParams
			.pipe(untilDestroyed(this))
			.subscribe((queryparams) => {
				const { modal } = queryparams;

				if (modal === 'submit') {
					this.openPortal();
				}
			});
	}

	changeStepperColor(state?: string): void {
		switch (state) {
			case 'failed':
				this.steps = this.steps.map((step) => ({
					...step,
					failed: true,
					warning: false,
				}));
				break;
			case 'warning':
				this.steps = this.steps.map((step) => ({
					...step,
					failed: false,
					warning: true,
				}));
				break;
			default:
				this.steps = this.steps.map((step) => ({
					...step,
					failed: false,
					warning: false,
				}));
				break;
		}
	}

	setCurrentStep(state: number) {
		switch (state) {
			case 0:
				this.currentStep = 0;
				break;
			case ChicagoStatus.NOT_SUBMITTED:
				this.currentStep = 0;
				break;
			case ChicagoStatus.UNDER_REVIEW:
				this.currentStep = 1;
				break;
			case ChicagoStatus.WAITING_FOR_INFORMATION:
				this.currentStep = 2;
				break;
			case ChicagoStatus.READY_FOR_PICKUP:
				this.currentStep = 3;
				break;
			case ChicagoStatus.CANCELLED:
				this.currentStep = 3;
				break;
		}
	}

	cancel() {
		const data: CancelModalData = {
			status: this.status,
		};

		const modalRef = this.dialog.open(CancelModalComponent, {
			data,
			maxWidth: '450px',
			width: '90%',
		});

		modalRef
			.afterClosed()
			.pipe(take(1))
			.subscribe((value) => {
				if (value) {
					this.matterDispatch.SetTitleInsurance(
						this.matterId,
						this.blankTitleInsurance,
					);
				}
			});
	}

	selectInsuranceProvider() {
		this.dialog.open(AddTitleInsuranceComponent);
	}

	openPortal() {
		if (this.status === ChicagoStatus.CANCELLED) {
			const data: CancelModalData = {
				status: ChicagoStatus.CANCELLED,
			};

			this.dialog.open(CancelModalComponent, {
				data,
				maxWidth: '450px',
				width: '90%',
			});

			return;
		}

		const data: ChicagoPortalData = {
			submitNewOrder: false,
		};

		if (!this.chicagoLawfirm) {
			const modalRef = this.dialog.open(ConfirmFirmModalComponent, {
				maxWidth: '600px',
				width: '90%',
			});

			modalRef
				.afterClosed()
				.pipe(take(1))
				.subscribe((value) => {
					if (value) {
						this.businessDispatch.SetChicagoFirmName(value);
						this.dialog.open(ChicagoPortalComponent, { data });
					}
				});

			return;
		}
		this.dialog.open(ChicagoPortalComponent, { data });
	}
}
